import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Upload, FileText, CheckCircle, XCircle, Trash2 } from 'lucide-react'
import { useTusUpload } from '@features/file-operations/hooks/useTusUpload'
import { useBulkUploadNotification } from '@features/file-operations/hooks/useBulkUploadNotification'
import { useNotificationContext } from '@contexts/NotificationContext'
import { formatBytes } from '@utils/formatBytes'
import FileDropZone from './FileDropZone'
import Modal from '@components/ui/Modal'

interface DocumentUploadModalProps {
  isOpen: boolean
  onClose: () => void
  folderId?: string
}

export default function DocumentUploadModal({
  isOpen,
  onClose,
  folderId,
}: DocumentUploadModalProps) {
  const { t } = useTranslation()
  const [isHidden, setIsHidden] = useState(false)

  const {
    files,
    addFiles,
    removeFile,
    updateFileName,
    clearAll,
  } = useTusUpload()

  const { showToast, updateToast, dismissToast } = useNotificationContext()

  const { startTracking } = useBulkUploadNotification({
    files,
    folderId,
    showToast,
    updateToast,
    dismissToast
  })

  const handleFilesSelected = (fileList: FileList) => {
    // Files will auto-upload in the hook
    addFiles(fileList)
  }


  // Format transfer rate (bytes per second)
  const formatTransferRate = (bytesPerSecond: number): string => {
    if (!bytesPerSecond || bytesPerSecond === 0) return ''

    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytesPerSecond) / Math.log(k))
    const rate = (bytesPerSecond / Math.pow(k, i)).toFixed(1)

    return `${rate} ${t(`fileExplorer.filters.units.${sizes[i]}`)}/s`
  }

  const handleSubmit = () => {
    if (files.length === 0) return

    // Start tracking upload progress in notification
    // The notification will track TUS uploads, then backend processing
    startTracking()

    // Hide modal but keep it mounted so uploads continue
    setIsHidden(true)
  }

  const handleClose = () => {
    // Only allow closing if not tracking or all files are done
    const hasActiveUploads = files.some(f => f.status === 'uploading')

    if (!hasActiveUploads) {
      clearAll()
      setIsHidden(false)
      onClose()
    }
  }

  // Auto-cleanup: when all uploads complete and modal is hidden, close it
  useEffect(() => {
    const allComplete = files.length > 0 && files.every(f => f.status === 'success' || f.status === 'error')
    if (isHidden && allComplete) {
      // Small delay to ensure backend processing starts
      const timer = setTimeout(() => {
        clearAll()
        setIsHidden(false)
        onClose()
      }, 1000)
      return () => clearTimeout(timer)
    }
  }, [isHidden, files, clearAll, onClose])

  const hasFiles = files.length > 0

  // Don't render modal if hidden (but component stays mounted)
  if (isHidden) {
    return null
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="xl"
      title={t('documents.upload_title')}
      icon={<Upload className="w-6 h-6 text-primary" />}
      disabled={false}
      footer={
        <>
          <button
            type="button"
            onClick={handleClose}
            className="btn btn-ghost"
            data-testid="cancel-upload-button"
          >
            {t('common.cancel')}
          </button>

          {hasFiles && (
            <button
              type="button"
              onClick={handleSubmit}
              className="btn btn-primary"
              data-testid="save-all-button"
            >
              <CheckCircle className="w-4 h-4" />
              {t('documents.save_all')}
            </button>
          )}
        </>
      }
      data-testid="upload-modal"
    >
      {/* Drop Zone - Always visible */}
      <div className="mb-6">
        <FileDropZone
          onFilesSelected={handleFilesSelected}
          disabled={false}
        />
      </div>

        {/* File List */}
        {hasFiles && (
          <div className="mb-6 space-y-3 max-h-96 overflow-y-auto" data-testid="file-list">
            <h4 className="font-semibold text-sm text-base-content/70 mb-2">
              {t('documents.file_list')} ({files.length})
            </h4>
            {files.map((file) => (
              <div
                key={file.id}
                className="border border-base-300 rounded-lg p-4"
                data-testid={`file-item-${file.id}`}
              >
                <div className="flex items-start gap-3">
                  <FileText className="w-5 h-5 text-base-content/50 mt-1 flex-shrink-0" />
                  <div className="flex-1 min-w-0 space-y-2">
                    <input
                      type="text"
                      value={file.name}
                      onChange={(e) => updateFileName(file.id, e.target.value)}
                      placeholder={t('documents.name_placeholder')}
                      className="input input-sm input-bordered w-full"
                      data-testid={`file-name-input-${file.id}`}
                    />

                    {/* Info row: size - type - progress/status - delete */}
                    <div className="flex items-center gap-3">
                      {/* File info */}
                      <div className="flex items-center gap-2 text-xs text-base-content/60">
                        <span>{formatBytes(file.size)}</span>
                        <span>•</span>
                        <span>{file.type || 'Unknown'}</span>
                      </div>

                      {/* Progress bar or status icon */}
                      <div className="flex-1 min-w-0 h-5 flex items-center">
                        {file.status === 'uploading' ? (
                          <div className="flex items-center gap-2 flex-1">
                            <progress
                              className="progress progress-primary flex-1"
                              value={file.progress}
                              max="100"
                            ></progress>
                            <div className="flex flex-col items-end text-xs text-base-content/60 min-w-20">
                              <span>{file.progress.toFixed(0)}%</span>
                              {file.transferRate && (
                                <span className="text-[10px] text-base-content/50">
                                  {formatTransferRate(file.transferRate)}
                                </span>
                              )}
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center justify-end w-full">
                            {file.status === 'success' && (
                              <CheckCircle className="w-5 h-5 text-success" />
                            )}
                            {file.status === 'error' && (
                              <XCircle className="w-5 h-5 text-error" />
                            )}
                          </div>
                        )}
                      </div>

                      {/* Delete button */}
                      {file.status !== 'uploading' && (
                        <button
                          type="button"
                          onClick={() => removeFile(file.id)}
                          className="btn btn-xs btn-ghost btn-circle flex-shrink-0"
                          title={t('common.delete')}
                          data-testid={`remove-file-${file.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>

                    {/* Error message */}
                    {file.error && (
                      <div className="alert alert-error alert-sm">
                        <span className="text-xs">{file.error}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
    </Modal>
  )
}
